import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * Product decision 2026-08-31 — long-dated trials (the absorbed grandfather
 * cohort, whose trials run to Jan-2027) stay silent until the last 30 days;
 * short admin-granted trials behave as before. Without the threshold an
 * absorbed clinic would carry a "129 days left in your free trial" banner from
 * its first login.
 */
export const TRIAL_BANNER_DAYS_REMAINING = 30;

/**
 * The single show/hide decision for the sidebar trial banner (called at the
 * render site in App.tsx — this component does no gating of its own). Anything
 * that is not a finite, still-running day count stays silent: null/undefined
 * (no trial, or the status call never answered) and 0-or-negative (the trial is
 * over — that case belongs to the paywall, not the banner).
 */
export function shouldShowTrialBanner(daysRemaining: number | null | undefined): daysRemaining is number {
  return (
    typeof daysRemaining === 'number' &&
    Number.isFinite(daysRemaining) &&
    daysRemaining > 0 &&
    daysRemaining <= TRIAL_BANNER_DAYS_REMAINING
  );
}

interface TrialBannerProps {
  daysRemaining: number;
}

const TrialBanner: React.FC<TrialBannerProps> = ({ daysRemaining }) => {
  const navigate = useNavigate();
  const isUrgent = daysRemaining <= 7;

  return (
    <Box
      sx={{
        mx: 1.5,
        mb: 1,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        bgcolor: isUrgent ? '#FFF3E0' : 'rgba(139,94,52,0.06)',
        border: '1px solid',
        borderColor: isUrgent ? '#FFB74D' : 'rgba(139,94,52,0.12)',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: isUrgent ? '#E65100' : '#5D4037',
          fontWeight: 600,
          lineHeight: 1.4,
        }}
      >
        {daysRemaining === 0
          ? 'Your free trial ends today'
          : daysRemaining === 1
            ? '1 day left in your free trial'
            : `${daysRemaining} days left in your free trial`}
      </Typography>
      <Button
        size="small"
        variant={isUrgent ? 'contained' : 'text'}
        onClick={() => navigate('/paywall')}
        sx={{
          mt: 0.75,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          p: 0,
          minWidth: 0,
          ...(isUrgent
            ? {
                px: 1.5,
                py: 0.25,
                bgcolor: '#C9772B',
                '&:hover': { bgcolor: '#6B3410' },
              }
            : {
                color: '#C9772B',
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }),
        }}
      >
        Choose a plan →
      </Button>
    </Box>
  );
};

export default TrialBanner;
